import React from 'react';

import FormModal from './FormModal';

import ActionButton from '../Buttons/ActionButton';
import InputIconByKey from '../Forms/Input/InputIconByKey';

import { DteErrorDetail } from '../../types/dte/DteErrorDetail';

// =====================================================
// 📦 Props
// =====================================================

interface Props {
  isOpen: boolean;
  detail?: DteErrorDetail | null;
  loading?: boolean;
  unavailable?: boolean;
  onDownload?: () => void;
  onClose: () => void;
}

// =====================================================
// 🧩 Componente
// =====================================================

const DteErrorModal: React.FC<Props> = ({
  isOpen,
  detail,
  loading = false,
  unavailable = false,
  onDownload,
  onClose,
}) => {
  // =====================================================
  // 🔹 Estado fiscal
  // =====================================================

  const normalizedStatus =
    detail?.status?.trim().toUpperCase() ?? '';

  const rejected =
    detail?.hasDteError === true ||
    normalizedStatus === 'RECHAZADO';

  const processed =
    !rejected &&
    (
      normalizedStatus === 'PROCESADO' ||
      normalizedStatus === 'PROCESSED'
    );

  // =====================================================
  // 🔹 Formatos
  // =====================================================

  const formatValue = (
    value?: string | null,
    fallback = 'No informado'
  ) => {
    if (!value || value.trim() === '') {
      return fallback;
    }

    return value;
  };

  const formatDate = (
    value?: string | null
  ) => {
    if (!value) {
      return 'No informada';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  const formatEnvironment = (
    environment?: string | null
  ) => {
    if (!environment) {
      return 'No informado';
    }

    if (environment === '00') {
      return 'Pruebas';
    }

    if (environment === '01') {
      return 'Producción';
    }

    return environment;
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        rejected
          ? 'Rechazo de Hacienda'
          : 'Respuesta de Hacienda'
      }
      maxWidth="max-w-4xl"
      overlayBg="black"
      zIndex="front"
    >
      {/* =================================================
          CARGANDO
          ================================================= */}

      {loading && (
        <div
          className="
            flex
            min-h-[220px]
            flex-col
            items-center
            justify-center
            gap-4
            text-center
          "
        >
          <div
            className="
              flex
              h-14
              w-14
              items-center
              justify-center

              rounded-full

              bg-primary/10
            "
          >
            <span className="animate-spin">
              <InputIconByKey
                iconKey="refresh"
                className="h-6 w-6 text-primary"
              />
            </span>
          </div>

          <div>
            <p className="font-medium text-black dark:text-white">
              Consultando respuesta
            </p>

            <p className="mt-1 text-sm text-body dark:text-bodydark">
              Obteniendo información registrada por el
              Ministerio de Hacienda.
            </p>
          </div>
        </div>
      )}

      {/* =================================================
          NO DISPONIBLE
          ================================================= */}

      {!loading && unavailable && (
        <div
          className="
            flex
            items-start
            gap-4

            rounded-xl
            border
            border-warning/30

            bg-warning/10

            px-5
            py-4
          "
        >
          <div
            className="
              flex
              h-10
              w-10
              shrink-0
              items-center
              justify-center

              rounded-xl

              bg-warning/15
            "
          >
            <InputIconByKey
              iconKey="warning"
              className="h-5 w-5 text-warning"
            />
          </div>

          <div>
            <p className="font-semibold text-black dark:text-white">
              Respuesta no disponible
            </p>

            <p className="mt-1 text-sm leading-6 text-body dark:text-bodydark">
              No fue posible obtener la respuesta registrada
              por Hacienda para este documento.
            </p>
          </div>
        </div>
      )}

      {/* =================================================
          SIN INFORMACIÓN
          ================================================= */}

      {!loading &&
        !unavailable &&
        !detail && (
          <div
            className="
              flex
              min-h-[180px]
              flex-col
              items-center
              justify-center
              text-center
            "
          >
            <div
              className="
                mb-3
                flex
                h-12
                w-12
                items-center
                justify-center

                rounded-full

                bg-gray-2
                dark:bg-meta-4
              "
            >
              <InputIconByKey
                iconKey="document"
                className="h-5 w-5 text-body dark:text-bodydark"
              />
            </div>

            <p className="font-medium text-black dark:text-white">
              Sin información
            </p>

            <p className="mt-1 text-sm text-body dark:text-bodydark">
              No existe información disponible para mostrar.
            </p>
          </div>
        )}

      {/* =================================================
          DETALLE
          ================================================= */}

      {!loading &&
        detail && (
          <div className="space-y-6">

            {/* =============================================
                ESTADO PRINCIPAL
                ============================================= */}

            <div
              className={`
                flex
                items-start
                gap-4

                rounded-xl
                border

                px-5
                py-4

                ${
                  rejected
                    ? `
                      border-danger/30
                      bg-danger/5
                    `
                    : `
                      border-success/30
                      bg-success/5
                    `
                }
              `}
            >
              <div
                className={`
                  flex
                  h-11
                  w-11
                  shrink-0
                  items-center
                  justify-center

                  rounded-xl

                  ${
                    rejected
                      ? 'bg-danger/10'
                      : 'bg-success/10'
                  }
                `}
              >
                <InputIconByKey
                  iconKey={
                    rejected
                      ? 'alert'
                      : 'check'
                  }
                  className={`
                    h-6
                    w-6

                    ${
                      rejected
                        ? 'text-danger'
                        : 'text-success'
                    }
                  `}
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">

                  <p
                    className={`
                      text-base
                      font-semibold

                      ${
                        rejected
                          ? 'text-danger'
                          : 'text-success'
                      }
                    `}
                  >
                    {rejected
                      ? 'Documento rechazado por Hacienda'
                      : processed
                        ? 'Documento procesado por Hacienda'
                        : 'Respuesta recibida de Hacienda'}
                  </p>

                  {detail.status && (
                    <span
                      className={`
                        rounded-full
                        border
                        px-2.5
                        py-1

                        text-xs
                        font-semibold

                        ${
                          rejected
                            ? `
                              border-danger/20
                              bg-danger/10
                              text-danger
                            `
                            : `
                              border-success/20
                              bg-success/10
                              text-success
                            `
                        }
                      `}
                    >
                      {detail.status}
                    </span>
                  )}
                </div>

                <p
                  className="
                    mt-2
                    text-sm
                    leading-6
                    text-black
                    dark:text-white
                  "
                >
                  {detail.message ||
                    (
                      rejected
                        ? 'El documento fue rechazado por el Ministerio de Hacienda.'
                        : 'El documento fue procesado correctamente por el Ministerio de Hacienda.'
                    )}
                </p>
              </div>
            </div>

            {/* =============================================
                INFORMACIÓN GENERAL
                ============================================= */}

            <section>

              <div
                className="
                  mb-4
                  flex
                  items-center
                  gap-3

                  border-b
                  border-stroke

                  pb-3

                  dark:border-strokedark
                "
              >
                <div
                  className="
                    flex
                    h-9
                    w-9
                    items-center
                    justify-center

                    rounded-xl

                    bg-primary/10
                  "
                >
                  <InputIconByKey
                    iconKey="document"
                    className="h-5 w-5 text-primary"
                  />
                </div>

                <div>
                  <h3
                    className="
                      text-base
                      font-semibold
                      text-black
                      dark:text-white
                    "
                  >
                    Información de procesamiento
                  </h3>

                  <p className="mt-0.5 text-xs text-body dark:text-bodydark">
                    Datos asociados a la respuesta recibida
                    desde Hacienda.
                  </p>
                </div>
              </div>

              <div
                className="
                  grid
                  overflow-hidden

                  rounded-xl
                  border
                  border-stroke

                  bg-white

                  dark:border-strokedark
                  dark:bg-meta-4

                  sm:grid-cols-2
                "
              >
                {/* Código */}

                <InfoItem
                  label="Código"
                  value={formatValue(detail.code)}
                  iconKey="hash"
                />

                {/* Estado */}

                <InfoItem
                  label="Estado"
                  value={formatValue(detail.status)}
                  iconKey="status"
                />

                {/* Fecha */}

                <InfoItem
                  label="Fecha y hora"
                  value={formatDate(detail.occurredAt)}
                  iconKey="calendar"
                />

                {/* Ambiente */}

                <InfoItem
                  label="Ambiente"
                  value={formatEnvironment(
                    detail.environment
                  )}
                  iconKey="server"
                />
              </div>
            </section>

            {/* =============================================
                IDENTIFICADORES
                ============================================= */}

            <section>

              <div
                className="
                  mb-4
                  flex
                  items-center
                  gap-3

                  border-b
                  border-stroke

                  pb-3

                  dark:border-strokedark
                "
              >
                <div
                  className="
                    flex
                    h-9
                    w-9
                    items-center
                    justify-center

                    rounded-xl

                    bg-primary/10
                  "
                >
                  <InputIconByKey
                    iconKey="code"
                    className="h-5 w-5 text-primary"
                  />
                </div>

                <div>
                  <h3
                    className="
                      text-base
                      font-semibold
                      text-black
                      dark:text-white
                    "
                  >
                    Identificadores fiscales
                  </h3>

                  <p className="mt-0.5 text-xs text-body dark:text-bodydark">
                    Identificadores relacionados con el DTE
                    procesado.
                  </p>
                </div>
              </div>

              <div
                className="
                  overflow-hidden

                  rounded-xl
                  border
                  border-stroke

                  dark:border-strokedark
                "
              >
                <TechnicalItem
                  label="Código de generación"
                  value={formatValue(
                    detail.generationCode
                  )}
                  iconKey="code"
                />

                <TechnicalItem
                  label="Sello de recepción"
                  value={formatValue(
                    detail.receptionSeal
                  )}
                  iconKey="shield"
                  borderTop
                />
              </div>

            </section>

            {/* =============================================
                OBSERVACIONES
                ============================================= */}

            <section>

              <div
                className="
                  mb-4
                  flex
                  items-center
                  gap-3

                  border-b
                  border-stroke

                  pb-3

                  dark:border-strokedark
                "
              >
                <div
                  className={`
                    flex
                    h-9
                    w-9
                    items-center
                    justify-center

                    rounded-xl

                    ${
                      rejected
                        ? 'bg-danger/10'
                        : 'bg-primary/10'
                    }
                  `}
                >
                  <InputIconByKey
                    iconKey={
                      rejected
                        ? 'warning'
                        : 'message'
                    }
                    className={`
                      h-5
                      w-5

                      ${
                        rejected
                          ? 'text-danger'
                          : 'text-primary'
                      }
                    `}
                  />
                </div>

                <div>
                  <h3
                    className="
                      text-base
                      font-semibold
                      text-black
                      dark:text-white
                    "
                  >
                    Observaciones
                  </h3>

                  <p className="mt-0.5 text-xs text-body dark:text-bodydark">
                    Información adicional proporcionada por
                    Hacienda durante el procesamiento.
                  </p>
                </div>
              </div>

              {detail.observations?.length ? (
                <div className="space-y-2">

                  {detail.observations.map(
                    (item, index) => (
                      <div
                        key={`${index}-${item}`}
                        className={`
                          flex
                          items-start
                          gap-3

                          rounded-xl
                          border

                          px-4
                          py-3

                          ${
                            rejected
                              ? `
                                border-danger/20
                                bg-danger/5
                              `
                              : `
                                border-stroke
                                bg-gray-1

                                dark:border-strokedark
                                dark:bg-meta-4
                              `
                          }
                        `}
                      >
                        <span
                          className={`
                            mt-0.5
                            flex
                            h-5
                            w-5
                            shrink-0
                            items-center
                            justify-center

                            rounded-full

                            text-[10px]
                            font-bold

                            ${
                              rejected
                                ? `
                                  bg-danger/10
                                  text-danger
                                `
                                : `
                                  bg-primary/10
                                  text-primary
                                `
                            }
                          `}
                        >
                          {index + 1}
                        </span>

                        <p
                          className="
                            min-w-0
                            flex-1

                            break-words

                            text-sm
                            leading-6
                            text-black

                            dark:text-white
                          "
                        >
                          {item}
                        </p>
                      </div>
                    )
                  )}

                </div>
              ) : (
                <div
                  className="
                    rounded-xl
                    border
                    border-stroke

                    bg-gray-1

                    px-4
                    py-3

                    text-sm
                    text-body

                    dark:border-strokedark
                    dark:bg-meta-4
                    dark:text-bodydark
                  "
                >
                  Sin observaciones adicionales.
                </div>
              )}

            </section>

            {/* =============================================
                DETALLE TÉCNICO
                ============================================= */}

            {detail.details &&
              Object.keys(detail.details).length > 0 && (
                <section>

                  <details
                    className="
                      overflow-hidden

                      rounded-xl
                      border
                      border-stroke

                      bg-white

                      dark:border-strokedark
                      dark:bg-meta-4
                    "
                  >
                    <summary
                      className="
                        flex
                        cursor-pointer
                        items-center
                        gap-3

                        px-4
                        py-3

                        font-medium
                        text-black

                        transition

                        hover:bg-gray-1

                        dark:text-white
                        dark:hover:bg-boxdark-2
                      "
                    >
                      <InputIconByKey
                        iconKey="terminal"
                        className="h-4 w-4 text-primary"
                      />

                      Ver detalle técnico
                    </summary>

                    <div
                      className="
                        border-t
                        border-stroke

                        bg-[#0f172a]

                        p-4

                        dark:border-strokedark
                      "
                    >
                      <pre
                        className="
                          max-h-72
                          overflow-auto

                          whitespace-pre-wrap
                          break-all

                          font-mono
                          text-xs
                          leading-5
                          text-slate-200
                        "
                      >
                        {JSON.stringify(
                          detail.details,
                          null,
                          2
                        )}
                      </pre>
                    </div>
                  </details>

                </section>
              )}

            {/* =============================================
                ACCIONES
                ============================================= */}

            <div
              className="
                flex
                flex-col-reverse
                gap-3

                border-t
                border-stroke

                pt-5

                dark:border-strokedark

                sm:flex-row
                sm:justify-end
              "
            >
              <div className="w-full sm:w-auto">
                <ActionButton
                  type="button"
                  label="Cerrar"
                  variant="secondary"
                  fullWidth={false}
                  icon={
                    <InputIconByKey
                      iconKey="no"
                      className="h-4 w-4"
                    />
                  }
                  onClick={onClose}
                />
              </div>

              {onDownload && (
                <div className="w-full sm:w-auto">

                  <ActionButton
                    type="button"
                    label="Descargar respuesta JSON"
                    variant="primary"
                    fullWidth={false}
                    icon={
                      <InputIconByKey
                        iconKey="document"
                        className="h-4 w-4 text-white"
                      />
                    }
                    onClick={onDownload}
                  />

                </div>
              )}
            </div>

          </div>
        )}
    </FormModal>
  );
};

// =====================================================
// 🧩 Información general
// =====================================================

interface InfoItemProps {
  label: string;
  value: string;
  iconKey:
    | 'hash'
    | 'status'
    | 'calendar'
    | 'server';
}

const InfoItem: React.FC<InfoItemProps> = ({
  label,
  value,
  iconKey,
}) => {
  return (
    <div
      className="
        flex
        min-h-[82px]
        items-start
        gap-3

        border-b
        border-stroke

        px-4
        py-4

        last:border-b-0

        dark:border-strokedark

        sm:border-b
        sm:odd:border-r
      "
    >
      <div
        className="
          flex
          h-9
          w-9
          shrink-0
          items-center
          justify-center

          rounded-lg

          bg-primary/10
        "
      >
        <InputIconByKey
          iconKey={iconKey}
          className="h-4 w-4 text-primary"
        />
      </div>

      <div className="min-w-0">
        <p
          className="
            text-xs
            font-medium
            text-body

            dark:text-bodydark
          "
        >
          {label}
        </p>

        <p
          className="
            mt-1
            break-words

            text-sm
            font-semibold
            text-black

            dark:text-white
          "
        >
          {value}
        </p>
      </div>
    </div>
  );
};

// =====================================================
// 🧩 Identificadores técnicos
// =====================================================

interface TechnicalItemProps {
  label: string;
  value: string;
  iconKey: 'code' | 'shield';
  borderTop?: boolean;
}

const TechnicalItem: React.FC<TechnicalItemProps> = ({
  label,
  value,
  iconKey,
  borderTop = false,
}) => {
  return (
    <div
      className={`
        flex
        items-start
        gap-4

        bg-white

        px-4
        py-4

        dark:bg-meta-4

        ${
          borderTop
            ? `
              border-t
              border-stroke

              dark:border-strokedark
            `
            : ''
        }
      `}
    >
      <div
        className="
          flex
          h-9
          w-9
          shrink-0
          items-center
          justify-center

          rounded-lg

          bg-primary/10
        "
      >
        <InputIconByKey
          iconKey={iconKey}
          className="h-4 w-4 text-primary"
        />
      </div>

      <div className="min-w-0 flex-1">

        <p
          className="
            text-xs
            font-medium
            text-body

            dark:text-bodydark
          "
        >
          {label}
        </p>

        <p
          className="
            mt-1.5

            break-all

            font-mono
            text-xs
            font-medium
            leading-5

            text-black

            dark:text-white
          "
        >
          {value}
        </p>

      </div>
    </div>
  );
};

export default DteErrorModal;